import { useRef, useState, type ChangeEvent, type DragEvent } from "react";
import { supabase } from "@/lib/supabase";
import { parsePhotoUrls, serializePhotoUrls } from "../adminConfig";
import PhotoLightbox from "./PhotoLightbox";

interface PhotoUploaderProps {
  value: string;
  onChange: (value: string) => void;
  max?: number;
}

const BUCKET = "member-photos";
const MAX_SIZE = 5 * 1024 * 1024;
const DEFAULT_MAX = 9;

export default function PhotoUploader({ value, onChange, max = DEFAULT_MAX }: PhotoUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const photos = parsePhotoUrls(value);

  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [dragging, setDragging] = useState(false);
  const [preview, setPreview] = useState<number | null>(null);

  const uploadFiles = async (files: File[]) => {
    setError("");
    if (files.length === 0) return;

    if (photos.length >= max) {
      setError(`最多上传 ${max} 张照片`);
      return;
    }

    const available = max - photos.length;
    const accepted: File[] = [];
    for (const file of files.slice(0, available)) {
      if (!file.type.startsWith("image/")) {
        setError("请选择图片文件（JPG / PNG / WebP）");
        continue;
      }
      if (file.size > MAX_SIZE) {
        setError("单张图片大小不能超过 5MB");
        continue;
      }
      accepted.push(file);
    }
    if (accepted.length === 0) return;

    setUploading(true);
    try {
      const uploaded: string[] = [];
      for (const file of accepted) {
        const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
        const safeExt = /^[a-z0-9]+$/.test(ext) ? ext : "jpg";
        const path = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${safeExt}`;
        const { error: uploadError } = await supabase.storage
          .from(BUCKET)
          .upload(path, file, { upsert: false, contentType: file.type });
        if (uploadError) throw uploadError;
        const { data } = supabase.storage.from(BUCKET).getPublicUrl(path);
        uploaded.push(data.publicUrl);
      }
      onChange(serializePhotoUrls([...photos, ...uploaded]));
    } catch (err) {
      setError(err instanceof Error ? err.message : "上传失败，请稍后重试");
    } finally {
      setUploading(false);
    }
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length) void uploadFiles(files);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    const files = Array.from(event.dataTransfer.files || []);
    if (files.length) void uploadFiles(files);
  };

  const removeAt = (index: number) => {
    onChange(serializePhotoUrls(photos.filter((_, i) => i !== index)));
    setPreview(null);
  };

  const canAddMore = photos.length < max;

  return (
    <div className="flex flex-col gap-3">
      {photos.length > 0 && (
        <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
          {photos.map((url, index) => (
            <div
              key={`${url}-${index}`}
              className="relative aspect-[3/4] overflow-hidden rounded-lg border border-background-200 bg-background-100"
            >
              <img
                src={url}
                alt={`会员照片 ${index + 1}`}
                className="h-full w-full cursor-zoom-in object-cover object-top"
                onClick={() => setPreview(index)}
              />
              {index === 0 && (
                <span className="absolute left-1.5 top-1.5 rounded bg-primary-500/90 px-1.5 py-0.5 text-[10px] font-medium text-background-50">
                  封面
                </span>
              )}
              <button
                type="button"
                onClick={() => removeAt(index)}
                className="absolute right-1.5 top-1.5 flex h-6 w-6 cursor-pointer items-center justify-center rounded-full bg-foreground-950/55 text-background-50 transition-colors hover:bg-accent-500"
                aria-label="移除这张照片"
              >
                <i className="ri-close-line text-sm"></i>
              </button>
            </div>
          ))}

          {canAddMore && (
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="flex aspect-[3/4] cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-background-300 bg-background-50 text-foreground-400 transition-colors hover:border-primary-300 hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {uploading ? (
                <i className="ri-loader-4-line animate-spin text-xl"></i>
              ) : (
                <i className="ri-add-line text-xl"></i>
              )}
              <span className="text-xs">添加</span>
            </button>
          )}
        </div>
      )}

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        className={`flex flex-col items-center justify-center rounded-lg border-2 border-dashed px-4 py-6 text-center transition-colors ${
          dragging ? "border-primary-400 bg-primary-50" : "border-background-300 bg-background-50"
        }`}
      >
        <p className="text-xs text-foreground-500">
          {photos.length === 0 ? "拖拽照片到此处，或" : "继续添加照片，或"}
        </p>
        <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading || !canAddMore}
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <i className="ri-upload-2-line"></i>
            {uploading ? "上传中…" : canAddMore ? "上传照片" : `已达 ${max} 张上限`}
          </button>
          {photos.length > 0 && (
            <button
              type="button"
              onClick={() => {
                setError("");
                onChange("");
              }}
              className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-xs font-medium text-foreground-600 transition-colors hover:border-accent-300 hover:text-accent-600"
            >
              <i className="ri-delete-bin-line"></i>
              全部移除
            </button>
          )}
        </div>
        <p className="mt-2 text-xs text-foreground-400">
          支持 JPG / PNG / WebP，单张不超过 5MB，最多 {max} 张，点击缩略图可看大图
        </p>
      </div>

      {error && <p className="text-xs text-accent-600">{error}</p>}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleInputChange}
        className="hidden"
      />

      {preview !== null && (
        <PhotoLightbox
          images={photos}
          index={preview}
          onClose={() => setPreview(null)}
          onIndexChange={setPreview}
        />
      )}
    </div>
  );
}